import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { generateContent } from '../services/api';
import { useProfile } from '../context/ProfileContext';

const LeadConverter = () => {
  const { getSignature, isLimitReached, incrementUsage } = useProfile();
  const [formData, setFormData] = useState({
    name: '',
    interest: '',
    budget: '',
    details: ''
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [copied, setCopied] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (isLimitReached) return;
    setLoading(true);
    setResult('');
    try {
      const generatedText = await generateContent('lead', formData);
      const signature = buildSignature();
      setResult(generatedText + '\n\n' + signature);
      incrementUsage();
    } catch (error) {
      setResult("Sorry, an error occurred while generating the email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const buildSignature = () => {
    const profile = JSON.parse(localStorage.getItem('cruisecopilot_agent_profile') || '{}');
    const name = (profile.firstName && profile.lastName) ? `${profile.firstName} ${profile.lastName}` : 'Your Cruise Specialist';
    const tagline = profile.tagline || null;
    const agency = profile.agencyName || 'Avoya Travel Network';
    const email = profile.email || null;
    const phone = profile.phone || null;
    const contactLine = [email, phone].filter(Boolean).join(' | ');

    return [
      'Warm regards,',
      name,
      tagline,
      agency,
      contactLine
    ].filter(Boolean).join('\n');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Form Section */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-xl font-semibold text-slate-800 mb-6 flex items-center gap-2">
          <SafeIcon name="UserPlus" className="text-blue-600" /> Client Details
        </h2>
        <form onSubmit={handleGenerate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Client Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              placeholder="e.g., Sarah Jenkins"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Cruise Interest</label>
              <select
                name="interest"
                value={formData.interest}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
              >
                <option value="">Not Specified</option>
                <option value="Caribbean">Caribbean</option>
                <option value="Mediterranean">Mediterranean</option>
                <option value="Alaska">Alaska</option>
                <option value="River Cruise (Europe)">River Cruise (Europe)</option>
                <option value="Expedition/Antarctica">Expedition/Antarctica</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Budget Range</label>
              <select
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
              >
                <option value="">Not Specified</option>
                <option value="Value (Under $1k)">Value (Under $1k)</option>
                <option value="Standard ($1k - $3k)">Standard ($1k - $3k)</option>
                <option value="Premium ($3k - $7k)">Premium ($3k - $7k)</option>
                <option value="Luxury ($7k+)">Luxury ($7k+)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Personal Detail / Notes</label>
            <textarea
              name="details"
              value={formData.details}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none"
              placeholder="e.g., Celebrating 10th wedding anniversary, traveling with 2 young kids, loves wine tasting..."
            ></textarea>
          </div>

          <button
            type="submit"
            disabled={loading || isLimitReached}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed mt-4 shadow-sm shadow-blue-100"
          >
            {loading ? <SafeIcon name="Loader" className="animate-spin" /> : <SafeIcon name="Sparkles" />}
            {loading ? 'Generating...' : isLimitReached ? 'Limit Reached' : 'Generate Email'}
          </button>
        </form>
      </div>

      {/* Result Section */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-6 flex flex-col h-full min-h-[400px]">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
            <SafeIcon name="Mail" className="text-blue-600" /> Generated Draft
          </h2>
          {result && (
            <button
              onClick={handleCopy}
              className="text-sm flex items-center gap-1.5 text-slate-600 hover:text-blue-600 transition-colors bg-white px-3 py-1.5 rounded-md border border-slate-200 shadow-sm"
            >
              {copied ? <SafeIcon name="Check" className="text-green-500" /> : <SafeIcon name="Copy" />}
              {copied ? 'Copied!' : 'Copy Text'}
            </button>
          )}
        </div>
        <div className="flex-grow bg-white border border-slate-200 rounded-lg p-5 overflow-y-auto whitespace-pre-wrap text-slate-700 leading-relaxed shadow-inner">
          {loading ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 space-y-3">
              <SafeIcon name="PenTool" className="w-8 h-8 animate-pulse text-blue-400" />
              <p>Crafting the perfect message...</p>
            </div>
          ) : result ? (
            result
          ) : (
            <div className="h-full flex items-center justify-center text-slate-400 text-center px-8">
              {isLimitReached ? 'You have reached your limit of 10 free generations.' : 'Fill out the details and click generate to create a personalized, high-converting first contact email.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadConverter;